import type { TFunction } from 'i18next';

/**
 * Possible states for the Meta advertising card.
 * - 'normal': Card is selectable (integration exists, not Lite tier)
 * - 'locked': Card is locked for Lite tier accounts
 * - 'setup': Card shows setup button (no Meta integration)
 */
export type TMetaCardState = 'normal' | 'locked' | 'setup';

/** Parameters for determining Meta card state */
export interface IGetMetaCardStateParams {
  /** Whether the account is on the Platform Lite tier */
  isPlatformLite: boolean;
  /** Whether the account has an existing Meta integration */
  hasExistingIntegration: boolean;
}

/**
 * Determines the state of the Meta advertising card based on account status.
 *
 * @param params - Object containing account status flags
 * @returns The appropriate Meta card state
 */
export function getMetaCardState(params: IGetMetaCardStateParams): TMetaCardState {
  const { isPlatformLite, hasExistingIntegration } = params;

  if (isPlatformLite) {
    return 'locked';
  }

  if (!hasExistingIntegration) {
    return 'setup';
  }

  return 'normal';
}

/**
 * Returns the tooltip text for a locked Meta card.
 *
 * @param t - Translation function
 * @returns Translated tooltip message
 */
export function getMetaLockedTooltip(t: TFunction): string {
  return t('Upgrade to Essential to access this feature.');
}

/**
 * Returns the description text for the Meta card when setup is required.
 *
 * @param t - Translation function
 * @returns Translated description message
 */
export function getMetaSetupDescription(t: TFunction): string {
  return t('Connect your Meta account to run ads on Facebook and Instagram');
}
